/**
 * FAR 135.267 duty-time projector for the Instant Charter Quote widget.
 *
 * This lives entirely on the client so the timeline stays live while the
 * customer edits legs, aircraft type, and departure times. Whatever comes
 * back from the server on submit remains the authoritative billing math.
 *
 * Rule model (2-pilot, IFR — most common charter case):
 *   • Duty period opens 1 hour BEFORE scheduled block-off
 *   • Duty period closes 30 minutes AFTER block-on
 *   • Max flight time  ≤ 10 h within a rolling 24-h period
 *   • Max duty period  ≤ 14 h from open to close
 *   • ≥ 10 h continuous rest between duty periods resets the counters
 *
 * We deliberately do NOT try to model rare "extension" waivers or the
 * one-pilot rule set — a real dispatcher does that. Our job is to give the
 * shopper the industry limits their operator is held to so they can build
 * a trip that is legal on its face.
 */

export const DUTY_RULES = {
  maxFlight24h: 10,       // hours
  maxDutyPeriod: 14,      // hours
  minRestForReset: 10,    // hours
  preDeparture: 60,       // minutes of duty before block-off
  postArrival: 30,        // minutes of duty after block-on
};

/**
 * Haversine great-circle distance in nautical miles.
 */
export function haversineNm(a, b) {
  const R_NM = 3440.065; // Earth radius in nm
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(a.lat), φ2 = toRad(b.lat);
  const dφ = toRad(b.lat - a.lat);
  const dλ = toRad(b.lng - a.lng);
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R_NM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Parse a { date: "YYYY-MM-DD", hour: "HH" } pair into an epoch ms in UTC.
 * We keep everything in a single frame of reference so add/subtract math
 * is trivial. The user is picking wall-clock times for display only.
 */
export function legDepartureMs(leg) {
  if (!leg?.date || leg?.hour == null) return null;
  const hh = String(leg.hour).padStart(2, "0");
  const iso = `${leg.date}T${hh}:00:00Z`;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Format an epoch ms back to "HH:MM · MM/DD" using UTC so the round-trip
 * matches what the user picked. Zero timezone gymnastics.
 */
export function formatWallTime(ms) {
  if (ms == null) return "—";
  const d = new Date(ms);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${hh}:${mm} · ${mo}/${day}`;
}

/**
 * "2h 45m" style formatter for flight time / remaining duty readouts.
 */
export function formatHM(hours) {
  if (hours == null || !Number.isFinite(hours)) return "—";
  if (hours <= 0) return "0h 00m";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 60) return `${h + 1}h 00m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/**
 * Build the duty-timeline plan across an ordered list of legs.
 *
 * @param {Array<{origin, destination, date, hour}>} legs
 * @param {number} cruiseKtas   Aircraft cruise speed
 * @param {Object<string, {lat, lng}>} coords  ICAO -> resolved coord
 *
 * @returns {{ready:boolean, plans:Array}} Where each plan entry is:
 *   {
 *     idx, ready, pending,
 *     origin, destination,
 *     distNm, flightHrs,
 *     dutyStartMs, blockOffMs, blockOnMs, dutyEndMs,
 *     newPeriod, restHrsSincePrev,
 *     cumFlightHrs, cumDutyHrs,
 *     remainingFlight, remainingDuty,
 *     flightBreach, dutyBreach,
 *     flightWarn, dutyWarn,
 *   }
 */
export function computeDutyPlan(legs, cruiseKtas, coords) {
  const plans = [];
  if (!Array.isArray(legs) || legs.length === 0 || !cruiseKtas) {
    return { ready: false, plans: [] };
  }

  let period = null; // active duty period accumulator
  let lastDutyEndMs = null;

  legs.forEach((leg, idx) => {
    const origin = (leg?.origin || "").toUpperCase();
    const destination = (leg?.destination || "").toUpperCase();
    const depMs = legDepartureMs(leg);
    const oCoord = origin ? coords[origin] : null;
    const dCoord = destination ? coords[destination] : null;

    if (!origin || !destination || !depMs) {
      plans.push({ idx, ready: false });
      return;
    }
    if (!oCoord || !dCoord) {
      plans.push({ idx, ready: false, pending: true, origin, destination });
      return;
    }

    const distNm = haversineNm(oCoord, dCoord);
    const flightHrs = distNm / cruiseKtas;

    const dutyStartMs = depMs - DUTY_RULES.preDeparture * 60_000;
    const blockOffMs = depMs;
    const blockOnMs = depMs + flightHrs * 3_600_000;
    const dutyEndMs = blockOnMs + DUTY_RULES.postArrival * 60_000;

    // Is this a continuation of the previous duty period or a fresh one
    // (with ≥ 10h rest since the last duty closed)?
    let newPeriod = !period;
    let restHrsSincePrev = null;
    if (period && lastDutyEndMs != null) {
      restHrsSincePrev = (dutyStartMs - lastDutyEndMs) / 3_600_000;
      if (restHrsSincePrev >= DUTY_RULES.minRestForReset) newPeriod = true;
    }
    if (newPeriod) {
      period = { startMs: dutyStartMs, flightHrsUsed: 0 };
    }
    period.flightHrsUsed += flightHrs;

    const cumDutyHrs = (dutyEndMs - period.startMs) / 3_600_000;
    const cumFlightHrs = period.flightHrsUsed;

    const remainingFlight = DUTY_RULES.maxFlight24h - cumFlightHrs;
    const remainingDuty = DUTY_RULES.maxDutyPeriod - cumDutyHrs;

    plans.push({
      idx,
      ready: true,
      origin, destination,
      distNm,
      flightHrs,
      dutyStartMs, blockOffMs, blockOnMs, dutyEndMs,
      newPeriod,
      restHrsSincePrev,
      cumFlightHrs,
      cumDutyHrs,
      remainingFlight,
      remainingDuty,
      flightBreach: cumFlightHrs > DUTY_RULES.maxFlight24h + 0.01,
      dutyBreach: cumDutyHrs > DUTY_RULES.maxDutyPeriod + 0.01,
      flightWarn: cumFlightHrs > DUTY_RULES.maxFlight24h - 1 && cumFlightHrs <= DUTY_RULES.maxFlight24h + 0.01,
      dutyWarn: cumDutyHrs > DUTY_RULES.maxDutyPeriod - 1 && cumDutyHrs <= DUTY_RULES.maxDutyPeriod + 0.01,
    });

    lastDutyEndMs = dutyEndMs;
  });

  return { ready: plans.some((p) => p.ready), plans };
}
